import { apiUrl } from "../../api/endpoints";
import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import DataGridTable from "../../Components/DataGridTable";
import Spinner from "../../Components/Spinner";

import { useAuth } from "../../hooks/AuthProvider";
import { useNavigate } from 'react-router-dom';
import Alert from "../../Components/Alert";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";

import { LanguageContext } from "../../context/LanguageContext";
import "./UserHealthDetails.css";
import {
  getHealthTranslations,
  HealthPageHeader,
  PermissionDenied,
  isRtlLanguage,
} from "./userHealthShared";

export const UserHealth = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);
  const [controlLoading, setControlLoading] = useState(false);
  const [openScanControls, setOpenScanControls] = useState(false);
  const [selectedSubid, setSelectedSubid] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedActiveScanId, setSelectedActiveScanId] = useState("");
  const [shareWithAdmin, setShareWithAdmin] = useState(true);
  const [healthProfile, setHealthProfile] = useState({
    customerId: "",
    name: "",
    sex: "M",
    age: "",
    height: "",
    phone: "",
  });
  const [controlData, setControlData] = useState({
    subscribers: [],
    users: [],
    devices: [],
  });
  const navigate = useNavigate();
  const { permissions, org, user } = useAuth();

  const { lang } = useContext(LanguageContext);
  const t = getHealthTranslations(lang);

  const canView = permissions["view_users_health"];
  const canControlScans = canView && ["owner", "super"].includes(String(user?.role || "").toLowerCase());
  const isSuper = String(user?.role || "").toLowerCase() === "super";

  const isRtl = isRtlLanguage(lang);
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: isRtl ? "left" : "right",
  });

  const pagingoptions = [10, 20, 50, 100];


  const columns = [
    {
      field: "userName",
      headerName: t.columns.user,
      width: 250,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "weight",
      headerName: t.columns.weight,
      width: 150,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "height",
      headerName: t.columns.height,
      width: 150,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "bmi",
      headerName: t.columns.bmi,
      width: 120,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "age",
      headerName: t.columns.age,
      width: 120,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "gmtCreate",
      headerName: t.columns.createdAt,
      width: 200,
      align: "center",
      headerAlign: "center"


    },
  ];

  const fetchData = useCallback(async () => {
    if (!canView) return;

    setLoading(true);

    try {
      const sort = sortModel?.[0] || {};
      const res = await axios.get(apiUrl(`user-health/by-org/${org.id}`), {
        params: {
          page,
          pageSize,
          sortField: sort.field || "id",
          sortOrder: sort.sort || "desc",
          filterModel: JSON.stringify(filterModel || { items: [] }),
        },
      });

      if (Array.isArray(res.data?.data)) {
        const formatted = res.data.data.map((item) => ({
          ...item,
          userName: item.user?.name || "",
        }));

        setRows(formatted);
        setRowCount(Number(res.data?.pagination?.total ?? formatted.length));
      } else {
        setRows([]);
        setRowCount(0);
      }
    } catch (err) {
      console.error("UserHealth fetch error:", err);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [canView, filterModel, org?.id, page, pageSize, sortModel]);

  const fetchControlData = useCallback(async (subid = selectedSubid) => {
    if (!canControlScans) return;

    try {
      setControlLoading(true);
      const res = await axios.get(apiUrl("medical-device/control-data"), {
        params: isSuper && subid ? { subid } : {},
      });
      const data = res.data?.DATA || {};
      setControlData({
        subscribers: data.subscribers || [],
        users: data.users || [],
        devices: data.devices || [],
      });
    } catch (err) {
      console.error("Health control data error:", err);
    } finally {
      setControlLoading(false);
    }
  }, [canControlScans, isSuper, selectedSubid]);

  const showAlert = (type, message) => {
    setAlertState({
      open: true,
      type,
      message,
      position: isRtl ? "left" : "right",
    });
  };

  const selectedUser = controlData.users.find((item) => Number(item.id) === Number(selectedUserId));
  const selectedUserScan = controlData.devices.find(
    (item) =>
      Number(item.user_id) === Number(selectedUserId) &&
      String(item.scanStatus || "").toLowerCase() === "scanning"
  );
  const activeScans = controlData.devices.filter(
    (item) => String(item.scanStatus || "").toLowerCase() === "scanning"
  );
  const selectedActiveScan =
    activeScans.find((item) => Number(item.id) === Number(selectedActiveScanId)) ||
    selectedUserScan ||
    activeScans[0];
  const availableDevice = controlData.devices.find(
    (item) =>
      Number(item.subid) === Number(selectedUser?.subid) &&
      String(item.scanStatus || "").toLowerCase() !== "scanning"
  );
  const hasExternalStatusWarning = controlData.devices.some((item) => item.externalStatusError);

  const applyUserProfile = (userRow) => {
    setHealthProfile({
      customerId: userRow?.healthCustomerId || userRow?.phone || "",
      name: userRow?.healthName || userRow?.name || "",
      sex: userRow?.healthSex || "M",
      age: userRow?.healthAge || "",
      height: userRow?.healthHeight || "",
      phone: userRow?.healthPhone || userRow?.phone || "",
    });
  };

  const handleProfileChange = (field, value) => {
    setHealthProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleStartScan = async () => {
    if (!selectedUser) return;

    try {
      setControlLoading(true);
      const res = await axios.post(apiUrl("medical-device/start-scan"), {
        subid: selectedUser.subid,
        userId: selectedUser.id,
        healthDeviceId: availableDevice?.id,
        isShared: shareWithAdmin,
        ...healthProfile,
      });
      showAlert("success", res.data?.INFO || "success");
      await fetchControlData(selectedSubid);
      setSelectedActiveScanId("");
    } catch (err) {
      const message = err?.response?.data?.INFO || err?.response?.data?.message || err.message || "Failed to start scan";
      showAlert("error", message);
    } finally {
      setControlLoading(false);
    }
  };

  const handleCancelScan = async () => {
    if (!selectedActiveScan?.id) return;

    try {
      setControlLoading(true);
      const res = await axios.post(apiUrl("medical-device/cancel-scan"), {
        subid: selectedActiveScan.subid || selectedUser?.subid,
        healthDeviceId: selectedActiveScan.id,
      });
      showAlert("success", res.data?.INFO || "success");
      await fetchControlData(selectedSubid);
      setSelectedActiveScanId("");
    } catch (err) {
      const message = err?.response?.data?.INFO || err?.response?.data?.message || err.message || "Failed to cancel scan";
      showAlert("error", message);
    } finally {
      setControlLoading(false);
    }
  };
  const handleRowClick = (params) => {
    navigate(`/users-health/details/${params.row.id}?from=users-health`, {
      state: { from: "/users-health", source: "users-health" },
    });
  };

  useEffect(() => {
    fetchData();
    fetchControlData();
  }, [fetchData, fetchControlData]);

  useEffect(() => {
    if (!selectedUserId && controlData.users.length) {
      const firstUser = controlData.users[0];
      setSelectedUserId(firstUser.id);
      applyUserProfile(firstUser);
    }
  }, [controlData.users, selectedUserId]);

  useEffect(() => {
    if (!selectedActiveScanId && activeScans.length) {
      setSelectedActiveScanId(activeScans[0].id);
    }
  }, [activeScans, selectedActiveScanId]);

  if (!canView) {
    return (
      <PermissionDenied title={t.states.permissionDenied} message={t.states.cannotViewHealth} />
    );
  }

  return (
    <div className="configuration devices-page health-page" dir={isRtl ? "rtl" : "ltr"} style={{ height: "100%" }}>
      <div className="device-main health-page-shell">
        <HealthPageHeader
          title={t.pages.records}
          subtitle={t.descriptions.records}
          actions={canControlScans ? (
            <button
              className="devices-primary-btn"
              onClick={() => {
                setOpenScanControls(true);
                fetchControlData(selectedSubid);
              }}
            >
              <SettingsInputComponentIcon style={{ marginInlineEnd: "8px" }} />
              <span>{t.actions.scanControls || "Scan Controls"}</span>
            </button>
          ) : null}
        />
        {openScanControls ? (
          <div id="modal" className="health-scan-modal-overlay" onMouseDown={() => setOpenScanControls(false)}>
            <div
              id="popup"
              className="dashboard-modal health-scan-modal health-scan-control-modal"
              onMouseDown={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="dashboard-header health-scan-modal__header">
                <div className="dashboard-header-text">
                  <h2>{t.actions.scanControls || "Scan Controls"}</h2>
                </div>
              </div>
              <div className="modal-divider" />
              <div className="modal-content dashboard-content health-scan-modal__content">
                <section className="health-control-panel health-control-panel--modal">
                  <div className="health-control-row">
                    {isSuper ? (
                      <label>
                        <span>{t.columns.subscriber}</span>
                        <select
                          value={selectedSubid}
                          onChange={(event) => {
                            setSelectedSubid(event.target.value);
                            setSelectedUserId("");
                            setSelectedActiveScanId("");
                            fetchControlData(event.target.value);
                          }}
                        >
                          <option value="">{t.actions.allSubscribers || "All subscribers"}</option>
                          {controlData.subscribers.map((subscriber) => (
                            <option key={subscriber.id} value={subscriber.id}>
                              {subscriber.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    <label className="health-control-user">
                      <span>{t.columns.user}</span>
                      <select
                        value={selectedUserId}
                        onChange={(event) => {
                          const nextUser = controlData.users.find((item) => Number(item.id) === Number(event.target.value));
                          setSelectedUserId(event.target.value);
                          applyUserProfile(nextUser);
                        }}
                      >
                        {controlData.users.map((item) => (
                          <option key={item.id} value={item.id}>
                            {isSuper ? `${item.subscriberName || item.subid} - ` : ""}{item.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>{t.actions.shareScanWithAdmin || "Share scan data with admin"}</span>
                      <input
                        type="checkbox"
                        checked={shareWithAdmin}
                        onChange={(event) => setShareWithAdmin(event.target.checked)}
                      />
                    </label>
                  </div>
                  <div className="health-profile-grid health-profile-grid--compact">
                    <label><span>{t.labels.user || "Name"}</span><input value={healthProfile.name} onChange={(event) => handleProfileChange("name", event.target.value)} /></label>
                    <label><span>{t.labels.phone || "Phone"}</span><input value={healthProfile.phone} onChange={(event) => handleProfileChange("phone", event.target.value)} /></label>
                    <label><span>{t.labels.sex}</span><select value={healthProfile.sex} onChange={(event) => handleProfileChange("sex", event.target.value)}><option value="M">M</option><option value="F">F</option></select></label>
                    <label><span>{t.labels.age}</span><input type="number" min="1" value={healthProfile.age} onChange={(event) => handleProfileChange("age", event.target.value)} /></label>
                    <label><span>{t.labels.height}</span><input type="number" min="1" step="0.1" value={healthProfile.height} onChange={(event) => handleProfileChange("height", event.target.value)} /></label>
                    <label><span>Customer ID</span><input value={healthProfile.customerId} onChange={(event) => handleProfileChange("customerId", event.target.value)} /></label>
                  </div>
                  <label className="health-active-scan-field">
                    <span>{t.actions.activeScan || "Active scan"}</span>
                    <select
                      value={selectedActiveScan?.id || ""}
                      onChange={(event) => setSelectedActiveScanId(event.target.value)}
                    >
                      {activeScans.length ? activeScans.map((item) => (
                        <option key={item.id} value={item.id}>
                          {isSuper ? `${item.subscriberName || item.subid} - ` : ""}
                          {item.userName || item.user_id || t.columns.user} / {item.deviceFrom || item.id}
                        </option>
                      )) : (
                        <option value="">{t.states.noActiveScan || "No active scan"}</option>
                      )}
                    </select>
                  </label>
                  <div className="health-control-actions">
                    <button
                      className="devices-primary-btn"
                      onClick={handleStartScan}
                      disabled={controlLoading || !selectedUser || !!selectedUserScan || !availableDevice}
                    >
                      <PlayArrowIcon style={{ marginInlineEnd: "8px" }} />
                      <span>{controlLoading ? (t.actions.starting || "Starting...") : (t.actions.startScan || "Start New Scan")}</span>
                    </button>
                    <button
                      className="devices-primary-btn health-danger-btn"
                      onClick={handleCancelScan}
                      disabled={controlLoading || !selectedActiveScan}
                    >
                      <CancelOutlinedIcon style={{ marginInlineEnd: "8px" }} />
                      <span>{controlLoading ? (t.actions.cancelling || "Cancelling...") : (t.actions.cancelScan || "Cancel Scan")}</span>
                    </button>
                    <span className="health-control-hint">
                      {selectedUserScan
                        ? `${t.labels.scanning}: ${selectedUserScan.deviceFrom || selectedUserScan.id}`
                        : availableDevice
                          ? `${t.labels.available}: ${availableDevice.deviceFrom || availableDevice.id}`
                          : (t.states.noDeviceAvailable || "No available health device")}
                    </span>
                    {hasExternalStatusWarning ? (
                      <span className="health-control-status-warning">
                        {t.labels.localFallback || "Status sync unavailable; start will reconcile with Anovator."}
                      </span>
                    ) : null}
                  </div>
                </section>
              </div>
              <div className="modal-divider" />
              <div className="modal-footer dashboard-footer health-scan-modal__footer">
                <div className="modal-footer-right-buttons">
                  <button
                    type="button"
                    className="btn btn-cancel-details"
                    onClick={() => setOpenScanControls(false)}
                  >
                    {t.actions.close || "Close"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <Alert
          open={alertState.open}
          close={() => setAlertState({ ...alertState, open: false })}
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
        />

        {loading && rows.length === 0 && rowCount === 0 && !(filterModel?.items || []).length ? (
          <Spinner />
        ) : rows.length > 0 || rowCount > 0 || (filterModel?.items || []).length > 0 ? (
          <div className="table-wrapper" style={{ flex: 1 }}>
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn={"id"}
              pagingoptions={pagingoptions}
              sortField="created_at"
              sortType="desc"
              showToolbar={true}
              tableKey="user_health"
              loading={loading}
              handleRowClick={handleRowClick}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel,
                onPageChange: setPage,
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(newModel && newModel.length ? newModel : []);
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}

            />
          </div>
        ) : (
          <div className="health-empty-state">{t.states.noData}</div>
        )}
      </div>
    </div>
  );
};
